import { useCallback } from 'react';
import { QueryClient, QueryKey } from '@tanstack/react-query';
import { useGetQrInvoices } from '@hooks/useGetQrInvoices/useGetQrInvoices';
import { useI18n } from '@hooks/useI18n/useI18n';
import { IMerchantSettingObservationViewConfigSetting } from '@schemas/merchantPortal/types/accountSettings';
import { IMerchantPortalTracking } from '@schemas/merchantPortal/types/tracking';
import { useSendCheckoutEmail, useSendCheckoutSms } from './queries';

interface IUseGetTrackingButtons {
  config: IMerchantSettingObservationViewConfigSetting | undefined;
  contractId?: string;
  data: IMerchantPortalTracking | undefined;
  queryClient: QueryClient;
  queryKey: QueryKey;
}

export const useGetTrackingButtons = ({
  config,
  contractId,
  data,
}: IUseGetTrackingButtons) => {
  const { translate } = useI18n();
  const lang = data?.languageCode;
  const { checkoutSmsLinkSent, deliveryChannel, status } = data || {};

  const { downloadQrInvoiceButton } = useGetQrInvoices({
    externalContractUuid: contractId,
    paymentMethod: data?.paymentMethod,
  });

  const sendCheckoutLinkViaSms = useSendCheckoutSms({
    externalContractUUID: contractId,
    lang,
  });

  const sendCheckoutLinkViaEmail = useSendCheckoutEmail({
    externalContractUUID: contractId,
    lang,
  });

  const printPage = useCallback(() => window.print(), []);

  const shouldShowSendSmsCheckoutBtn =
    deliveryChannel === 'DELAYED_SMS' &&
    status === 'initialised' &&
    !checkoutSmsLinkSent;

  const shouldShowSendEmailCheckoutBtn =
    deliveryChannel === 'DELAYED_EMAIL' &&
    status === 'initialised' &&
    !checkoutSmsLinkSent;

  const buttons = [
    ...(config?.showPrintButton
      ? [
          {
            clickHandler: printPage,
            'data-html2canvas-ignore': true,
            buttonKey: 'printPage',
            text: translate('BUTTON.PRINT'),
          },
        ]
      : []),
    ...(shouldShowSendSmsCheckoutBtn
      ? [
          {
            clickHandler: sendCheckoutLinkViaSms,
            buttonKey: 'sendSms',
            text: translate('BUTTON.SEND_CHECKOUT_SMS'),
          },
        ]
      : []),
    ...(shouldShowSendEmailCheckoutBtn
      ? [
          {
            clickHandler: sendCheckoutLinkViaEmail,
            buttonKey: 'sendEmail',
            text: translate('BUTTON.SEND_CHECKOUT_EMAIL'),
          },
        ]
      : []),
    ...downloadQrInvoiceButton,
  ].filter(Boolean);

  return { buttons };
};
